import {
  type ColumnFiltersState,
  type FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState
} from '@tanstack/react-table'
import { useState } from 'react'
import type { UseDataTableOptions, UseDataTableReturn } from './types'

function extractSearchableText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    return value.map(extractSearchableText).filter(Boolean).join(' ')
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map(extractSearchableText)
      .filter(Boolean)
      .join(' ')
  }
  return ''
}

const globalSearchFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const search = String(filterValue).toLowerCase()
  if (!search) return true
  const cellValue = row.getValue(columnId)
  const text = extractSearchableText(cellValue).toLowerCase()
  return text.includes(search)
}

export function useDataTable<TData, TValue = unknown>({
  columns,
  data,
  enableSorting = true,
  enableColumnVisibility = false,
  enableRowSelection = false,
  enableColumnResizing = false,
  getRowId,
  manualPagination = false,
  pageCount,
  rowCount,
  initialPageSize = 10,
  debugTable = false
}: UseDataTableOptions<TData, TValue>): UseDataTableReturn<TData> {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState<string>('')

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(enableSorting && {
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      enableMultiSort: true,
      isMultiSortEvent: () => true
    }),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility
    }),
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection
    }),
    ...(enableColumnResizing && {
      enableColumnResizing: true,
      columnResizeMode: 'onChange' as const
    }),
    ...(manualPagination && {
      manualPagination: true,
      pageCount,
      rowCount
    }),
    initialState: {
      pagination: { pageSize: initialPageSize }
    },
    ...(getRowId && { getRowId }),
    globalFilterFn: globalSearchFilter,
    debugTable,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter
    }
  })

  return { table, sorting, columnFilters, columnVisibility, rowSelection, setGlobalFilter }
}
